import { isString, omit, pare, validate, type MapRec, type Requires } from '@yp/common/identity.ts';
import type { StrictPropertyList } from '@yp/common/schema/descriptor.ts';
import { modals } from 'svelte-modals';
import type { EmptyForm, FormType } from '../forms/fields.ts';
import type { ActionHandler, DialogAction, DialogForm } from './modals.ts';
import { PromptModal, type PromptModalProps } from './prompt_modal.ts';

type PromptOptions = Partial<
  Pick<PromptModalProps<'', EmptyForm, string>, 'color' | 'icon' | 'text'>
>;
// { color?: string; icon?: string; prompt?: string | PromptLine[] };

export function confirm(header: string, options?: PromptOptions & { confirm?: string }) {
  return new Promise<boolean>((resolve) => {
    void modals.open(PromptModal, {
      text: 'Are you sure?',
      ...options,
      header,
      actions: ['confirm'],
      labels: pare({ confirm: options?.confirm }),
      cb: (action) => resolve(action === 'confirm'),
    } as PromptModalProps<'confirm', EmptyForm, string>);
  });
}

export function promptOptions<T extends string, CS extends string>(
  header: string,
  actions: T[],
  options?:
    | PromptOptions
    | Partial<Pick<PromptModalProps<T, EmptyForm, CS>, 'labels' | 'schemes' | 'colorMap'>>,
) {
  return new Promise<DialogAction<T>>((resolve) => {
    modals.open(PromptModal, {
      ...options,
      header,
      actions,
      cb: resolve,
    } as PromptModalProps<T, EmptyForm, CS>);
  });
}

type SimplePrompt = {
  answer: string;
};

export function promptAnswer(
  header: string,
  options: Requires<PromptOptions, 'text'> & {
    label?: string;
    default?: string | undefined;
  },
): Promise<string | undefined> {
  const simplePromptDesc: StrictPropertyList<SimplePrompt> = [
    { k: 'answer', t: 'string', req: true, label: options.label },
  ];
  const prompt: Partial<SimplePrompt> = pare({ answer: options?.default });
  return new Promise<string | undefined>((resolve) => {
    const form = $state({ value: prompt, desc: simplePromptDesc });
    modals.open(PromptModal, {
      ...omit(options, 'label', 'default'),
      header,
      form,
      actions: ['ok'],
      cb: (action) => resolve(action === 'ok' ? validate(form.value.answer, isString) : undefined),
    } as PromptModalProps<'ok', SimplePrompt, string>);
  });
}

export function post<F extends FormType, T extends string = never>(
  header: string,
  options: PromptOptions & {
    form: Requires<DialogForm<F>, 'action'>;
    actions?: Array<DialogAction<T>>;
    labels?: Partial<MapRec<DialogAction<T>, string>> | undefined;
    actionHandlers?: Partial<MapRec<DialogAction<'ok' | T>, ActionHandler<'ok' | T>>>;
  },
): Promise<DialogAction<'ok' | T>> {
  return new Promise<DialogAction<'ok' | T>>((resolve) => {
    // const color = options.color;
    // const icon = options.icon;
    // const text = options.text;
    // const form = options.form;
    const actions: DialogAction<'ok' | T>[] = ['ok', ...(options?.actions ?? [])];
    // const labels = options.labels;
    // const actionHandlers = options.actionHandlers;
    const cb: (action: DialogAction<'ok' | T>) => void = resolve;
    modals.open(
      PromptModal,
      pare({
        ...options,
        // color,
        // icon,
        header,
        // text,
        // form,
        actions,
        // labels,
        // actionHandlers,
        cb,
      } as PromptModalProps<'ok' | T, F, string>),
    );
  });
}

export function fill<F extends FormType, T extends string = never>(
  header: string,
  options: PromptOptions & {
    form: DialogForm<F>;
    actions?: Array<DialogAction<T>>;
    labels?: Partial<MapRec<DialogAction<T>, string>> | undefined;
    actionHandlers?: Partial<MapRec<DialogAction<'ok' | T>, ActionHandler<'ok' | T>>>;
  },
): Promise<DialogAction<'ok' | T>> {
  return new Promise<DialogAction<'ok' | T>>((resolve) => {
    const color = options.color;
    const icon = options.icon;
    const text = options.text;
    const form = options.form;
    const actions: DialogAction<'ok' | T>[] = ['ok', ...(options?.actions ?? [])];
    const labels = options.labels;
    const actionHandlers = options.actionHandlers;
    const cb: (action: DialogAction<'ok' | T>) => void = resolve;
    modals.open(
      PromptModal,
      pare({
        color,
        icon,
        header,
        text,
        form,
        actions,
        labels,
        actionHandlers,
        cb,
      } as PromptModalProps<'ok' | T, F, string>),
    );
  });
}
